// src/pages/SuperAdminPage.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService, roleService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { FEATURE_CATALOGUE, PLAN_FEATURE_DEFAULTS, planIncludes } from '../../constants/featureCatalogue';

// ---------------------------------------------------------------------------
// Small reusable helpers
// ---------------------------------------------------------------------------

const Toggle = ({ checked, onChange, disabled = false }) => (
    <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none ${
            checked ? 'bg-blue-600' : 'bg-gray-300'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
        <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
                checked ? 'translate-x-4' : 'translate-x-0'
            }`}
        />
    </button>
);

const Alert = ({ type, message, onClose }) => {
    if (!message) return null;
    const colors = {
        success: 'bg-green-50 border-green-400 text-green-800',
        error: 'bg-red-50 border-red-400 text-red-800',
    };
    return (
        <div className={`border-l-4 p-3 rounded flex items-start justify-between ${colors[type] || colors.error}`}>
            <span className="text-sm">{message}</span>
            {onClose && (
                <button onClick={onClose} className="ml-3 text-gray-500 hover:text-gray-700 text-lg leading-none">&times;</button>
            )}
        </div>
    );
};

const Spinner = () => (
    <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
);

const SectionHeader = ({ title, action }) => (
    <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        {action}
    </div>
);

const Th = ({ children, className = '', onClick }) => (
    <th className={`px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide bg-gray-50 ${className}`} onClick={onClick}>
        {children}
    </th>
);

const Td = ({ children, className = '' }) => (
    <td className={`px-3 py-2 text-sm text-gray-700 border-t border-gray-100 ${className}`}>
        {children}
    </td>
);

const Input = ({ label, ...props }) => (
    <div>
        {label && <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>}
        <input
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
            {...props}
        />
    </div>
);

const Select = ({ label, children, ...props }) => (
    <div>
        {label && <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>}
        <select
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
            {...props}
        >
            {children}
        </select>
    </div>
);

const Btn = ({ children, variant = 'primary', size = 'sm', className = '', ...props }) => {
    const variants = {
        primary: 'bg-blue-600 hover:bg-blue-700 text-white',
        secondary: 'bg-white hover:bg-gray-50 text-gray-700 border border-gray-300',
        danger: 'bg-red-600 hover:bg-red-700 text-white',
        ghost: 'bg-transparent hover:bg-gray-100 text-gray-600',
    };
    const sizes = {
        xs: 'px-2 py-1 text-xs',
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-sm',
    };
    return (
        <button
            className={`rounded font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${sizes[size]} ${className}`}
            {...props}
        >
            {children}
        </button>
    );
};

// ---------------------------------------------------------------------------
// CredentialsPanel — shown inline when editing a tenant
// ---------------------------------------------------------------------------

const PasswordCell = ({ password }) => {
    const { t } = useLanguage();
    const [visible, setVisible] = useState(false);
    return (
        <div className="flex items-center gap-1">
            <span className="font-mono text-xs">{visible ? password : '••••••••'}</span>
            <button type="button" onClick={() => setVisible(v => !v)}
                className="text-gray-400 hover:text-gray-600 p-0.5" title={visible ? t('hide') : t('show')}>
                {visible ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/></svg>
                )}
            </button>
        </div>
    );
};

const CredentialsPanel = ({ tenantId }) => {
    const { t } = useLanguage();
    const [credentials, setCredentials] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [addForm, setAddForm] = useState({ username: '', password: '', role_id: '', display_name: '' });
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null);
    const [editData, setEditData] = useState({});

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [credData, roleData] = await Promise.all([
                adminService.getCredentials(tenantId),
                roleService.getRoles(tenantId).catch(() => []),
            ]);
            setCredentials(credData);
            setRoles(roleData);
        } catch (e) {
            setAlert({ type: 'error', message: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
        }
    }, [tenantId]);

    useEffect(() => { load(); }, [load]);

    const getRoleName = (roleId) => {
        const r = roles.find(x => x.id === roleId);
        return r ? r.name : '—';
    };

    const handleAddCredential = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await adminService.createCredential({
                username: addForm.username,
                password: addForm.password,
                entity_id: tenantId,
                role_id: addForm.role_id ? parseInt(addForm.role_id) : null,
                display_name: addForm.display_name || null,
            });
            setAlert({ type: 'success', message: t('credential_created') });
            setShowAddForm(false);
            setAddForm({ username: '', password: '', role_id: '', display_name: '' });
            load();
        } catch (err) {
            setAlert({ type: 'error', message: err?.response?.data?.detail || err.message });
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (cred) => {
        setEditId(cred.id);
        setEditData({
            username: cred.username || '',
            display_name: cred.display_name || '',
            password: cred.password || '',
            role_id: cred.role_id || '',
            is_active: !!cred.is_active,
        });
    };

    const cancelEdit = () => { setEditId(null); setEditData({}); };

    const saveEdit = async () => {
        setSaving(true);
        try {
            await adminService.updateCredential(editId, {
                username: editData.username,
                display_name: editData.display_name || null,
                password: editData.password,
                role_id: editData.role_id ? parseInt(editData.role_id) : null,
                is_active: editData.is_active,
            });
            setAlert({ type: 'success', message: t('credential_updated') });
            cancelEdit();
            load();
        } catch (err) {
            setAlert({ type: 'error', message: err?.response?.data?.detail || err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(t('delete_credential_confirm'))) return;
        try {
            await adminService.deleteCredential(id);
            setAlert({ type: 'success', message: t('credential_deleted') });
            load();
        } catch (err) {
            setAlert({ type: 'error', message: err?.response?.data?.detail || err.message });
        }
    };

    const inputCls = "border border-gray-300 rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-blue-500";

    return (
        <div className="mt-6 border-t border-gray-200 pt-4">
            <SectionHeader
                title={t('credentials')}
                action={
                    <Btn variant="secondary" size="xs" onClick={() => setShowAddForm(v => !v)}>
                        {showAddForm ? t('Cancel') : t('add_credential')}
                    </Btn>
                }
            />

            {alert && <div className="mb-3"><Alert {...alert} onClose={() => setAlert(null)} /></div>}

            {showAddForm && (
                <form onSubmit={handleAddCredential} className="mb-4 p-3 bg-gray-50 rounded border border-gray-200 grid grid-cols-2 gap-3">
                    <Input label={t('username')} required value={addForm.username} onChange={e => setAddForm(f => ({ ...f, username: e.target.value }))} />
                    <Input label={t('password')} required value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} />
                    <Select label={t('role')} value={addForm.role_id} onChange={e => setAddForm(f => ({ ...f, role_id: e.target.value }))}>
                        <option value="">{t('select_role')}</option>
                        {roles.filter(r => r.is_active).map(r => (
                            <option key={r.id} value={r.id}>{r.name}</option>
                        ))}
                    </Select>
                    <Input label={t('display_name')} value={addForm.display_name} onChange={e => setAddForm(f => ({ ...f, display_name: e.target.value }))} placeholder={t('optional')} />
                    <div className="col-span-2 flex justify-end gap-2">
                        <Btn type="submit" disabled={saving}>{saving ? t('saving') : t('create')}</Btn>
                    </div>
                </form>
            )}

            {loading ? <Spinner /> : (
                <div className="overflow-x-auto">
                    <table className="w-full border border-gray-200 rounded text-sm">
                        <thead>
                            <tr>
                                <Th>{t('username')}</Th>
                                <Th>{t('display_name')}</Th>
                                <Th>{t('password')}</Th>
                                <Th>{t('role')}</Th>
                                <Th>{t('active')}</Th>
                                <Th>{t('actions')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {credentials.length === 0 ? (
                                <tr><Td colSpan={6} className="text-center text-gray-400 py-4">{t('no_credentials')}</Td></tr>
                            ) : credentials.map(cred => (
                                <tr key={cred.id} className="hover:bg-gray-50">
                                    {editId === cred.id ? (
                                        <>
                                            <Td><input value={editData.username} onChange={e => setEditData(d => ({ ...d, username: e.target.value }))} className={inputCls} /></Td>
                                            <Td><input value={editData.display_name} onChange={e => setEditData(d => ({ ...d, display_name: e.target.value }))} className={inputCls} placeholder={t('display_name')} /></Td>
                                            <Td><input value={editData.password} onChange={e => setEditData(d => ({ ...d, password: e.target.value }))} className={inputCls} /></Td>
                                            <Td>
                                                <select value={editData.role_id} onChange={e => setEditData(d => ({ ...d, role_id: e.target.value }))}
                                                    className="border border-gray-200 rounded px-2 py-1 text-xs bg-white w-full">
                                                    <option value="">—</option>
                                                    {roles.filter(r => r.is_active).map(r => (
                                                        <option key={r.id} value={r.id}>{r.name}</option>
                                                    ))}
                                                </select>
                                            </Td>
                                            <Td><Toggle checked={editData.is_active} onChange={v => setEditData(d => ({ ...d, is_active: v }))} /></Td>
                                            <Td>
                                                <div className="flex items-center gap-1">
                                                    <Btn size="xs" onClick={saveEdit} disabled={saving}>{saving ? '...' : t('save')}</Btn>
                                                    <Btn size="xs" variant="ghost" onClick={cancelEdit}>{t('Cancel')}</Btn>
                                                </div>
                                            </Td>
                                        </>
                                    ) : (
                                        <>
                                            <Td className="font-medium">{cred.username}</Td>
                                            <Td>{cred.display_name || '—'}</Td>
                                            <Td><PasswordCell password={cred.password} /></Td>
                                            <Td>{getRoleName(cred.role_id)}</Td>
                                            <Td>
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                                    cred.is_active ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
                                                }`}>{cred.is_active ? t('active') : t('inactive')}</span>
                                            </Td>
                                            <Td>
                                                <div className="flex items-center gap-2">
                                                    <Btn size="xs" variant="secondary" onClick={() => startEdit(cred)}>{t('edit')}</Btn>
                                                    <Btn size="xs" variant="danger" onClick={() => handleDelete(cred.id)}>{t('delete')}</Btn>
                                                </div>
                                            </Td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// EmployeesPanel — shown inline when editing a tenant (below credentials)
// ---------------------------------------------------------------------------

const EmployeesPanel = ({ tenantId }) => {
    const { t } = useLanguage();
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);
    const [search, setSearch] = useState('');
    const [sortField, setSortField] = useState('name');
    const [sortDir, setSortDir] = useState('asc');
    const [editId, setEditId] = useState(null);
    const [editData, setEditData] = useState({});
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminService.getEmployees(tenantId);
            setEmployees(data);
        } catch (e) {
            setAlert({ type: 'error', message: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
        }
    }, [tenantId]);

    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() => {
        let list = [...employees];
        if (search) {
            const q = search.toLowerCase();
            list = list.filter(e =>
                e.name?.toLowerCase().includes(q) ||
                e.employee_id?.toLowerCase().includes(q) ||
                e.role?.toLowerCase().includes(q) ||
                e.username?.toLowerCase().includes(q) ||
                e.pin?.includes(q)
            );
        }
        list.sort((a, b) => {
            const av = (a[sortField] || '').toString().toLowerCase();
            const bv = (b[sortField] || '').toString().toLowerCase();
            return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
        });
        return list;
    }, [employees, search, sortField, sortDir]);

    const toggleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const SortTh = ({ field, children }) => (
        <Th className="cursor-pointer select-none" onClick={() => toggleSort(field)}>
            <span className="inline-flex items-center gap-1">
                {children}
                {sortField === field && <span className="text-blue-500">{sortDir === 'asc' ? '↑' : '↓'}</span>}
            </span>
        </Th>
    );

    const startEdit = (emp) => {
        setEditId(emp.employee_id);
        setEditData({
            name: emp.name || '',
            role: emp.role || '',
            pin: emp.pin || '',
            status: emp.status || 'Active',
            is_cashier: !!emp.is_cashier,
            system_role: emp.system_role || 'staff',
        });
    };

    const cancelEdit = () => {
        setEditId(null);
        setEditData({});
    };

    const saveEdit = async () => {
        setSaving(true);
        try {
            await adminService.updateEmployee(editId, tenantId, editData);
            setAlert({ type: 'success', message: t('employee_updated') });
            cancelEdit();
            load();
        } catch (err) {
            setAlert({ type: 'error', message: err?.response?.data?.detail || err.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mt-6 border-t border-gray-200 pt-4">
            <SectionHeader title={`${t('employees')} (${employees.length})`} />

            {alert && <div className="mb-3"><Alert {...alert} onClose={() => setAlert(null)} /></div>}

            <div className="mb-3">
                <input
                    type="text"
                    placeholder={t('search_employees_placeholder')}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    className="w-full max-w-sm border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
            </div>

            {loading ? <Spinner /> : (
                <div className="overflow-x-auto">
                    <table className="w-full border border-gray-200 rounded text-sm">
                        <thead>
                            <tr>
                                <SortTh field="employee_id">{t('id')}</SortTh>
                                <SortTh field="name">{t('name')}</SortTh>
                                <SortTh field="role">{t('role')}</SortTh>
                                <Th>{t('pin')}</Th>
                                <SortTh field="status">{t('status')}</SortTh>
                                <Th>{t('cashier')}</Th>
                                <Th>{t('actions')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr><Td colSpan={7} className="text-center text-gray-400 py-4">{employees.length === 0 ? t('no_employees') : t('no_matches')}</Td></tr>
                            ) : filtered.map(emp => (
                                <tr key={emp.employee_id} className="hover:bg-gray-50">
                                    {editId === emp.employee_id ? (
                                        <>
                                            <Td className="font-mono text-xs">{emp.employee_id}</Td>
                                            <Td>
                                                <input value={editData.name} onChange={e => setEditData(d => ({ ...d, name: e.target.value }))}
                                                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full" />
                                            </Td>
                                            <Td>
                                                <input value={editData.role} onChange={e => setEditData(d => ({ ...d, role: e.target.value }))}
                                                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-full" />
                                            </Td>
                                            <Td>
                                                <input value={editData.pin} onChange={e => setEditData(d => ({ ...d, pin: e.target.value }))}
                                                    className="border border-gray-300 rounded px-1.5 py-0.5 text-xs w-20 font-mono" maxLength={6} />
                                            </Td>
                                            <Td>
                                                <select value={editData.status} onChange={e => setEditData(d => ({ ...d, status: e.target.value }))}
                                                    className="border border-gray-200 rounded px-1.5 py-0.5 text-xs bg-white">
                                                    <option value="Active">{t('active')}</option>
                                                    <option value="Inactive">{t('inactive')}</option>
                                                </select>
                                            </Td>
                                            <Td>
                                                <Toggle checked={editData.is_cashier} onChange={v => setEditData(d => ({ ...d, is_cashier: v }))} />
                                            </Td>
                                            <Td>
                                                <div className="flex items-center gap-1">
                                                    <Btn size="xs" onClick={saveEdit} disabled={saving}>{saving ? '...' : t('save')}</Btn>
                                                    <Btn size="xs" variant="ghost" onClick={cancelEdit}>{t('Cancel')}</Btn>
                                                </div>
                                            </Td>
                                        </>
                                    ) : (
                                        <>
                                            <Td className="font-mono text-xs">{emp.employee_id}</Td>
                                            <Td className="font-medium">{emp.name}</Td>
                                            <Td>{emp.role}</Td>
                                            <Td className="font-mono">{emp.pin || '—'}</Td>
                                            <Td>
                                                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                                    emp.status?.toLowerCase() === 'active'
                                                        ? 'bg-green-50 text-green-700'
                                                        : 'bg-gray-100 text-gray-500'
                                                }`}>
                                                    {emp.status}
                                                </span>
                                            </Td>
                                            <Td>{emp.is_cashier ? t('yes') : '—'}</Td>
                                            <Td>
                                                <Btn size="xs" variant="secondary" onClick={() => startEdit(emp)}>{t('edit')}</Btn>
                                            </Td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};


const LIMIT_CATALOGUE = [
    { key: 'max_dining_options', label: 'Max dining options' },
    { key: 'max_categories',     label: 'Max menu categories' },
];
const LIMIT_PLAN_DEFAULTS = {
    basic:   { max_dining_options: 3,    max_categories: 6    },
    pro:     { max_dining_options: null, max_categories: null },
    premium: { max_dining_options: null, max_categories: null },
};

// ---------------------------------------------------------------------------
// TenantForm — add / edit tenant
// ---------------------------------------------------------------------------

const genShortIdTenant = () => Math.floor(100000 + Math.random() * 900000);

const EMPTY_TENANT_FORM = {
    tenant_id: '',
    short_id: '',
    company_id: '',
    brand_name: '',
    long_brand_name: '',
    currency: '',
    tax_rate: '',
    service_charge_rate: '',
    receipt_header: '',
    receipt_footer: '',
    address: '',
    phone: '',
    email: '',
    languages: ['en'],
    plan_type: 'basic',
    brand_color: '#3B82F6',
    features: { table_service: false, takeaway: true, delivery: false, loyalty_program: false },
    payment_methods: [],
    admin_username: '',
    admin_password: '',
};

const TenantForm = ({ tenant, companies, onSave, onCancel }) => {
    const { t } = useLanguage();
    const isEdit = !!tenant;
    const [form, setForm] = useState(() => {
        if (isEdit) {
            return {
                ...EMPTY_TENANT_FORM,
                tenant_id: tenant.tenant_id || '',
                company_id: tenant.company_id || '',
                brand_name: tenant.brand_name || '',
                long_brand_name: tenant.long_brand_name || '',
                currency: tenant.currency || '',
                tax_rate: tenant.tax_rate != null ? String(tenant.tax_rate) : '',
                service_charge_rate: tenant.service_charge_rate != null ? String(tenant.service_charge_rate) : '',
                receipt_header: tenant.receipt_header || '',
                receipt_footer: tenant.receipt_footer || '',
                address: tenant.address || '',
                phone: tenant.phone || '',
                email: tenant.email || '',
                plan_type: tenant.plan_type || 'basic',
                brand_color: tenant.brand_color || '#3B82F6',
                features: tenant.features || { table_service: false, takeaway: true, delivery: false, loyalty_program: false },
                payment_methods: Array.isArray(tenant.payment_methods) ? tenant.payment_methods : [],
                languages: Array.isArray(tenant.languages) ? tenant.languages : ['en'],
            };
        }
        return { ...EMPTY_TENANT_FORM, short_id: genShortIdTenant() };
    });
    const [featureOverrides, setFeatureOverrides] = useState(() =>
        isEdit ? (tenant.feature_overrides || {}) : {}
    );
    const [limitOverrides, setLimitOverrides] = useState(() =>
        isEdit ? (tenant.limit_overrides || {}) : {}
    );
    const [receiptLogoFile, setReceiptLogoFile] = useState(null);
    const [brandLogoFile, setBrandLogoFile] = useState(null);
    const [clearReceiptLogo, setClearReceiptLogo] = useState(false);
    const [clearBrandLogo, setClearBrandLogo] = useState(false);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState(null);

    const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const setOverride = (flag, choice) => {
        setFeatureOverrides(prev => {
            const next = { ...prev };
            if (choice === 'default') {
                delete next[flag];
            } else {
                next[flag] = choice === 'on';
            }
            return next;
        });
    };

    const setLimitOverride = (key, value) => {
        setLimitOverrides(prev => {
            const next = { ...prev };
            if (value === 'remove') {
                delete next[key];
            } else {
                next[key] = value; // null for explicit unlimited, Number for a specific value
            }
            return next;
        });
    };

    const resetToDefaults = () => {
        setFeatureOverrides({});
        setLimitOverrides({});
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setAlert(null);
        try {
            const payload = {
                ...form,
                tax_rate: form.tax_rate !== '' ? parseFloat(form.tax_rate) : undefined,
                service_charge_rate: form.service_charge_rate !== '' ? parseFloat(form.service_charge_rate) : undefined,
                languages: Array.isArray(form.languages) ? form.languages : [],
                feature_overrides: featureOverrides,
                limit_overrides: limitOverrides,
            };
            if (isEdit) {
                // Remove create-only fields
                delete payload.admin_username;
                delete payload.admin_password;
                delete payload.tenant_id; // not editable
            }

            const result = await onSave(payload, receiptLogoFile, brandLogoFile, clearReceiptLogo, clearBrandLogo);
            if (result?.error) {
                setAlert({ type: 'error', message: result.error });
            }
        } catch (err) {
            setAlert({ type: 'error', message: err?.response?.data?.detail || err.message });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white border border-gray-200 rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">{isEdit ? `${t('edit_tenant')}: ${tenant.tenant_id}` : t('add_tenant')}</h3>
                <Btn variant="ghost" onClick={onCancel}>&times; {t('close')}</Btn>
            </div>

            {alert && <div className="mb-4"><Alert {...alert} onClose={() => setAlert(null)} /></div>}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <Input label={t('tenant_id')} required value={form.tenant_id} onChange={e => set('tenant_id', e.target.value)} disabled={isEdit} />
                    {!isEdit && (
                        <Input label={t('login_id_6_digits')} required inputMode="numeric" maxLength={6} value={form.short_id} onChange={e => set('short_id', parseInt(e.target.value) || '')} placeholder={t('prefilled_random_change')} />
                    )}
                    <Select label={t('company')} value={form.company_id} onChange={e => set('company_id', e.target.value)}>
                        <option value="">{t('select_company')}</option>
                        {companies.map(c => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Select>
                    <Input label={t('brand_name')} value={form.brand_name} onChange={e => set('brand_name', e.target.value)} />
                    <Input label={t('long_brand_name')} value={form.long_brand_name} onChange={e => set('long_brand_name', e.target.value)} />
                    <Input label={t('currency')} value={form.currency} onChange={e => set('currency', e.target.value)} placeholder={t('eg_usd')} />
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('brand_color')}</label>
                        <input type="color" value={form.brand_color} onChange={e => set('brand_color', e.target.value)} className="h-9 w-full border border-gray-300 rounded cursor-pointer" />
                    </div>
                    <Select label={t('plan_type')} value={form.plan_type} onChange={e => set('plan_type', e.target.value)}>
                        <option value="basic">{t('basic')}</option>
                        <option value="pro">{t('pro')}</option>
                        <option value="premium">{t('premium')}</option>
                    </Select>
                    <Input label={t('tax_rate')} type="number" step="0.01" min="0" max="1" value={form.tax_rate} onChange={e => set('tax_rate', e.target.value)} />
                    <Input label={t('service_charge_rate')} type="number" step="0.01" min="0" max="1" value={form.service_charge_rate} onChange={e => set('service_charge_rate', e.target.value)} />
                    <Input label={t('receipt_header')} value={form.receipt_header} onChange={e => set('receipt_header', e.target.value)} />
                    <Input label={t('receipt_footer')} value={form.receipt_footer} onChange={e => set('receipt_footer', e.target.value)} />
                    <Input label={t('address')} value={form.address} onChange={e => set('address', e.target.value)} />
                    <Input label={t('phone')} value={form.phone} onChange={e => set('phone', e.target.value)} />
                    <Input label={t('email')} type="email" value={form.email} onChange={e => set('email', e.target.value)} />
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('languages')}</label>
                        <div className="flex gap-3">
                            {[{ value: 'en', label: t('english') }, { value: 'ar', label: t('arabic') }].map(lang => (
                                <label key={lang.value} className="flex items-center gap-1 text-sm cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={Array.isArray(form.languages) && form.languages.includes(lang.value)}
                                        onChange={e => {
                                            const langs = Array.isArray(form.languages) ? [...form.languages] : [];
                                            if (e.target.checked) {
                                                if (!langs.includes(lang.value)) set('languages', [...langs, lang.value]);
                                            } else {
                                                if (langs.length > 1) set('languages', langs.filter(l => l !== lang.value));
                                            }
                                        }}
                                    />
                                    {lang.label}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Feature Access — tri-state overrides */}
                <div>
                    <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-medium text-gray-700">Feature Access</label>
                        <button
                            type="button"
                            onClick={resetToDefaults}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline"
                        >
                            Reset to plan defaults
                        </button>
                    </div>
                    <div className="border border-gray-200 rounded overflow-hidden">
                        {FEATURE_CATALOGUE.map((feat, idx) => {
                            const hasOverride = Object.prototype.hasOwnProperty.call(featureOverrides, feat.flag);
                            const overrideVal = featureOverrides[feat.flag];
                            const currentChoice = hasOverride ? (overrideVal ? 'on' : 'off') : 'default';
                            const planOn = planIncludes(form.plan_type, feat.flag);
                            return (
                                <div
                                    key={feat.flag}
                                    className={`flex items-center justify-between px-3 py-2 text-sm ${
                                        idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                    } ${hasOverride ? 'border-l-2 border-blue-500' : ''}`}
                                >
                                    {/* Label + tier tag + plan hint */}
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-gray-800 text-xs">{feat.label}</span>
                                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                                            feat.tier === 'Premium'
                                                ? 'bg-purple-100 text-purple-700'
                                                : 'bg-blue-100 text-blue-700'
                                        }`}>{feat.tier}</span>
                                        {hasOverride && (
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">Override</span>
                                        )}
                                        <span className="text-xs text-gray-400 hidden sm:inline">
                                            Plan: {planOn ? 'On' : 'Off'}
                                        </span>
                                    </div>

                                    {/* Tri-state segmented control */}
                                    <div className="flex items-center rounded border border-gray-200 overflow-hidden flex-shrink-0 ml-3">
                                        {[
                                            { value: 'default', label: 'Default' },
                                            { value: 'on',      label: 'On'      },
                                            { value: 'off',     label: 'Off'     },
                                        ].map(opt => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => setOverride(feat.flag, opt.value)}
                                                className={`px-2 py-1 text-xs font-medium transition-colors border-r last:border-r-0 border-gray-200 ${
                                                    currentChoice === opt.value
                                                        ? opt.value === 'on'
                                                            ? 'bg-green-600 text-white'
                                                            : opt.value === 'off'
                                                                ? 'bg-red-500 text-white'
                                                                : 'bg-gray-700 text-white'
                                                        : 'bg-white text-gray-500 hover:bg-gray-100'
                                                }`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Limits — per-key numeric overrides */}
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">Limits</label>
                    <div className="border border-gray-200 rounded overflow-hidden">
                        {LIMIT_CATALOGUE.map((limit, idx) => {
                            const planDefault = LIMIT_PLAN_DEFAULTS[form.plan_type]?.[limit.key];
                            const hasKey = Object.prototype.hasOwnProperty.call(limitOverrides, limit.key);
                            const currentVal = limitOverrides[limit.key];
                            const isUnlimited = hasKey && currentVal === null;
                            const numericVal = hasKey && currentVal !== null ? String(currentVal) : '';
                            const placeholder = planDefault === null ? 'Unlimited' : String(planDefault);
                            return (
                                <div
                                    key={limit.key}
                                    className={`flex items-center justify-between px-3 py-2 text-sm ${
                                        idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                                    } ${hasKey ? 'border-l-2 border-blue-500' : ''}`}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-gray-800 text-xs">{limit.label}</span>
                                        {hasKey && (
                                            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-600 font-medium">Override</span>
                                        )}
                                        <span className="text-xs text-gray-400 hidden sm:inline">
                                            Plan default: {planDefault === null ? 'Unlimited' : planDefault}
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                        <input
                                            type="number"
                                            min="1"
                                            step="1"
                                            disabled={isUnlimited}
                                            value={numericVal}
                                            placeholder={placeholder}
                                            onChange={e => {
                                                const v = e.target.value;
                                                if (v === '' || v.trim() === '') {
                                                    setLimitOverride(limit.key, 'remove');
                                                } else {
                                                    const n = parseInt(v, 10);
                                                    if (isNaN(n) || n < 1) {
                                                        setLimitOverride(limit.key, 'remove');
                                                    } else {
                                                        setLimitOverride(limit.key, n);
                                                    }
                                                }
                                            }}
                                            className="w-20 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400"
                                        />
                                        <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer select-none whitespace-nowrap">
                                            <input
                                                type="checkbox"
                                                checked={isUnlimited}
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        setLimitOverride(limit.key, null);
                                                    } else {
                                                        setLimitOverride(limit.key, 'remove');
                                                    }
                                                }}
                                                className="rounded"
                                            />
                                            Unlimited
                                        </label>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Logo uploads */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('receipt_logo')}</label>
                        <p className="text-xs text-gray-400 mb-1">{t('receipt_logo_recommendation')}</p>
                        {isEdit && tenant.receipt_logo && !clearReceiptLogo && (
                            <div className="flex items-center gap-2 mb-1">
                                <img src={tenant.receipt_logo} alt="receipt logo" className="h-8 w-auto border rounded" />
                                <button type="button" onClick={() => setClearReceiptLogo(true)} className="text-xs text-red-600 hover:underline">{t('remove')}</button>
                            </div>
                        )}
                        {isEdit && clearReceiptLogo && (
                            <p className="text-xs text-red-500 mb-1">{t('logo_removed_on_save')} <button type="button" onClick={() => setClearReceiptLogo(false)} className="underline">{t('undo')}</button></p>
                        )}
                        {receiptLogoFile ? (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600 truncate max-w-[140px]">{receiptLogoFile.name}</span>
                                <button type="button" onClick={() => setReceiptLogoFile(null)} className="text-xs text-red-600 hover:underline">{t('clear')}</button>
                            </div>
                        ) : (
                            <input
                                type="file"
                                accept="image/png,image/jpeg"
                                onChange={e => { setReceiptLogoFile(e.target.files[0] || null); setClearReceiptLogo(false); }}
                                className="text-xs text-gray-700"
                            />
                        )}
                    </div>
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">{t('brand_logo')}</label>
                        <p className="text-xs text-gray-400 mb-1">{t('brand_logo_recommendation')}</p>
                        {isEdit && tenant.brand_logo && !clearBrandLogo && (
                            <div className="flex items-center gap-2 mb-1">
                                <img src={tenant.brand_logo} alt="brand logo" className="h-8 w-auto border rounded" />
                                <button type="button" onClick={() => setClearBrandLogo(true)} className="text-xs text-red-600 hover:underline">{t('remove')}</button>
                            </div>
                        )}
                        {isEdit && clearBrandLogo && (
                            <p className="text-xs text-red-500 mb-1">{t('logo_removed_on_save')} <button type="button" onClick={() => setClearBrandLogo(false)} className="underline">{t('undo')}</button></p>
                        )}
                        {brandLogoFile ? (
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-600 truncate max-w-[140px]">{brandLogoFile.name}</span>
                                <button type="button" onClick={() => setBrandLogoFile(null)} className="text-xs text-red-600 hover:underline">{t('clear')}</button>
                            </div>
                        ) : (
                            <input
                                type="file"
                                accept="image/png,image/jpeg"
                                onChange={e => { setBrandLogoFile(e.target.files[0] || null); setClearBrandLogo(false); }}
                                className="text-xs text-gray-700"
                            />
                        )}
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-2">{t('payment_methods')}</label>
                    <div className="flex flex-wrap gap-4">
                        {[
                            { value: 'Cash', label: t('cash') },
                            { value: 'Card', label: t('card') },
                            { value: 'Mobile', label: t('mobile') },
                            { value: 'Split Payment', label: t('split_payment') },
                        ].map(({ value: method, label }) => (
                            <label key={method} className="flex items-center gap-1 text-sm cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={Array.isArray(form.payment_methods) && form.payment_methods.includes(method)}
                                    onChange={e => {
                                        const methods = Array.isArray(form.payment_methods) ? [...form.payment_methods] : [];
                                        if (e.target.checked) {
                                            if (!methods.includes(method)) set('payment_methods', [...methods, method]);
                                        } else {
                                            set('payment_methods', methods.filter(m => m !== method));
                                        }
                                    }}
                                />
                                {label}
                            </label>
                        ))}
                    </div>
                </div>

                {/* Create-only fields */}
                {!isEdit && (
                    <div className="grid grid-cols-2 gap-4 border-t border-gray-200 pt-4">
                        <p className="col-span-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">{t('initial_admin_credentials')}</p>
                        <Input label={t('admin_username')} required value={form.admin_username} onChange={e => set('admin_username', e.target.value)} />
                        <Input label={t('admin_password')} type="password" required value={form.admin_password} onChange={e => set('admin_password', e.target.value)} />
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2 border-t border-gray-200">
                    <Btn type="button" variant="secondary" onClick={onCancel}>{t('Cancel')}</Btn>
                    <Btn type="submit" disabled={saving}>{saving ? t('saving') : (isEdit ? t('update_tenant') : t('create_tenant'))}</Btn>
                </div>
            </form>

            {/* Credentials and Employees panels only shown when editing */}
            {isEdit && <CredentialsPanel tenantId={tenant.tenant_id} />}
            {isEdit && <EmployeesPanel tenantId={tenant.tenant_id} />}
        </div>
    );
};

// ---------------------------------------------------------------------------
// TenantsTab
// ---------------------------------------------------------------------------

const TenantsTab = ({ companies }) => {
    const { t } = useLanguage();
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);
    const [formMode, setFormMode] = useState(null); // null | 'add' | tenant object

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminService.getTenants();
            // Normalize: backend returns {id, config: {...}} — flatten for table/form use
            const normalized = data.map(tn => ({
                ...tn,
                tenant_id: tn.id,
                brand_name: tn.config?.brand_name || '',
                long_brand_name: tn.config?.long_brand_name || '',
                currency: tn.config?.currency || '',
                plan_type: tn.config?.plan_type || '',
                tax_rate: tn.config?.tax_rate ?? '',
                receipt_logo: tn.config?.receipt_logo || '',
                brand_logo: tn.config?.brand_logo || '',
                brand_color: tn.config?.brand_color || '#3B82F6',
                features: tn.config?.features || {},
                payment_methods: tn.config?.payment_methods || [],
                service_charge_rate: tn.config?.service_charge_rate ?? '',
                receipt_header: tn.config?.receipt_header || '',
                receipt_footer: tn.config?.receipt_footer || '',
                address: tn.config?.address || '',
                phone: tn.config?.phone || '',
                email: tn.config?.email || '',
                languages: tn.config?.languages || ['en'],
                feature_overrides: tn.config?.feature_overrides || {},
                limit_overrides: tn.config?.limit_overrides || {},
            }));
            setTenants(normalized);
        } catch (e) {
            setAlert({ type: 'error', message: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleToggleActive = async (tenant) => {
        try {
            await adminService.updateTenantStatus(tenant.tenant_id, !tenant.is_active);
            setTenants(prev => prev.map(tn => tn.tenant_id === tenant.tenant_id ? { ...tn, is_active: !tn.is_active } : tn));
        } catch (e) {
            setAlert({ type: 'error', message: e?.response?.data?.detail || e.message });
        }
    };

    const handleSave = async (payload, receiptLogoFile, brandLogoFile, clearReceiptLogo, clearBrandLogo) => {
        const extractError = (e) => {
            const detail = e?.response?.data?.detail;
            if (Array.isArray(detail)) return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
            return detail || e.message;
        };

        try {
            let tenantId;
            if (formMode === 'add') {
                // Backend expects {tenant_id, company_id, short_id, username, password, config: {...}}
                const { tenant_id, company_id, short_id, admin_username, admin_password, ...configFields } = payload;
                const structured = {
                    tenant_id,
                    company_id,
                    short_id: short_id ? parseInt(short_id) : null,
                    username: admin_username,
                    password: admin_password,
                    config: configFields,
                };
                const result = await adminService.createTenant(structured);
                tenantId = result.id || tenant_id;
                setAlert({ type: 'success', message: t('tenant_created') });
            } else {
                tenantId = formMode.tenant_id;
                // PUT accepts flat config merge — strip non-config fields
                const { tenant_id: _id, company_id, admin_username: _u, admin_password: _p, ...configFields } = payload;
                await adminService.updateTenant(tenantId, { ...configFields, company_id });
                setAlert({ type: 'success', message: t('tenant_updated') });
            }

            // Clear logos if requested (non-fatal)
            if (clearReceiptLogo) {
                try { await adminService.updateTenant(tenantId, { receipt_logo: '' }); } catch (_) {}
            }
            if (clearBrandLogo) {
                try { await adminService.updateTenant(tenantId, { brand_logo: '' }); } catch (_) {}
            }

            // Upload logos if files provided (non-fatal — show warning but don't block save)
            const logoWarnings = [];
            if (receiptLogoFile) {
                try {
                    await adminService.uploadTenantLogo(tenantId, receiptLogoFile, 'receipt');
                } catch (e) {
                    logoWarnings.push(`${t('receipt_logo')}: ${extractError(e)}`);
                }
            }
            if (brandLogoFile) {
                try {
                    await adminService.uploadTenantLogo(tenantId, brandLogoFile, 'brand');
                } catch (e) {
                    logoWarnings.push(`${t('brand_logo')}: ${extractError(e)}`);
                }
            }
            if (logoWarnings.length > 0) {
                setAlert({ type: 'error', message: `${t('saved_but_logo_upload_failed')}: ${logoWarnings.join(' | ')}` });
                setFormMode(null);
                load();
                return;
            }

            setFormMode(null);
            load();
        } catch (e) {
            const msg = extractError(e);
            setAlert({ type: 'error', message: msg });
            return { error: msg };
        }
    };

    const companyNameMap = Object.fromEntries(companies.map(c => [String(c.id), c.name]));

    return (
        <div>
            {alert && (
                <div className="mb-4">
                    <Alert {...alert} onClose={() => setAlert(null)} />
                </div>
            )}

            {formMode ? (
                <TenantForm
                    tenant={formMode === 'add' ? null : formMode}
                    companies={companies}
                    onSave={handleSave}
                    onCancel={() => setFormMode(null)}
                />
            ) : (
                <>
                    <SectionHeader
                        title={t('tenants')}
                        action={<Btn onClick={() => setFormMode('add')}>{t('add_tenant')}</Btn>}
                    />

                    {loading ? <Spinner /> : (
                        <div className="overflow-x-auto">
                            <table className="w-full border border-gray-200 rounded text-sm">
                                <thead>
                                    <tr>
                                        <Th>{t('tenant_id')}</Th>
                                        <Th>{t('login_id')}</Th>
                                        <Th>{t('brand_name')}</Th>
                                        <Th>{t('company')}</Th>
                                        <Th>{t('currency')}</Th>
                                        <Th>{t('plan')}</Th>
                                        <Th>{t('active')}</Th>
                                        <Th>{t('actions')}</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tenants.length === 0 ? (
                                        <tr><Td colSpan={7} className="text-center text-gray-400 py-6">{t('no_tenants_found')}</Td></tr>
                                    ) : tenants.map(tn => {
                                        const company = companies.find(c => c.id === tn.company_id);
                                        const companySuspended = company && !company.is_active;
                                        return (
                                        <tr key={tn.tenant_id} className={`hover:bg-gray-50 ${companySuspended ? 'opacity-60' : ''}`}>
                                            <Td><span className="font-mono text-xs">{tn.tenant_id}</span></Td>
                                            <Td><span className="font-mono text-sm font-semibold text-green-700">{tn.short_id ?? '—'}</span></Td>
                                            <Td>{tn.brand_name}</Td>
                                            <Td>
                                                <span>{companyNameMap[String(tn.company_id)] || tn.company_id || '—'}</span>
                                                {companySuspended && <span className="ml-1 text-xs text-red-500">({t('inactive')})</span>}
                                            </Td>
                                            <Td>{tn.currency || '—'}</Td>
                                            <Td>{tn.plan_type ? tn.plan_type.charAt(0).toUpperCase() + tn.plan_type.slice(1) : '—'}</Td>
                                            <Td>
                                                <Toggle checked={!!tn.is_active && !companySuspended} onChange={() => !companySuspended && handleToggleActive(tn)} disabled={companySuspended} />
                                            </Td>
                                            <Td>
                                                <Btn size="xs" variant="secondary" onClick={() => setFormMode(tn)}>{t('edit')}</Btn>
                                            </Td>
                                        </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// CompaniesTab
// ---------------------------------------------------------------------------

const CompaniesTab = () => {
    const { t } = useLanguage();
    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [alert, setAlert] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const genShortId = () => Math.floor(100000 + Math.random() * 900000);
    const [addForm, setAddForm] = useState({ id: '', name: '', short_id: genShortId() });
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState('');
    const [saving, setSaving] = useState(false);
    const [expandedCompany, setExpandedCompany] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await adminService.getCompanies();
            setCompanies(data);
        } catch (e) {
            setAlert({ type: 'error', message: e?.response?.data?.detail || e.message });
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleAdd = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await adminService.createCompany(addForm);
            setAlert({ type: 'success', message: t('company_created') });
            setShowAddForm(false);
            setAddForm({ id: '', name: '', short_id: genShortId() });
            load();
        } catch (err) {
            setAlert({ type: 'error', message: err?.response?.data?.detail || err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleEditSave = async (id) => {
        if (!editName.trim()) return;
        setSaving(true);
        try {
            await adminService.updateCompany(id, { name: editName });
            setAlert({ type: 'success', message: t('company_updated') });
            setEditingId(null);
            load();
        } catch (err) {
            setAlert({ type: 'error', message: err?.response?.data?.detail || err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (company) => {
        try {
            await adminService.updateCompanyStatus(company.id, !company.is_active);
            setCompanies(prev => prev.map(c => c.id === company.id ? { ...c, is_active: !c.is_active } : c));
        } catch (e) {
            setAlert({ type: 'error', message: e?.response?.data?.detail || e.message });
        }
    };

    const handleDelete = async (company) => {
        if (!window.confirm(t('delete_company_confirm', { name: company.name }))) return;
        try {
            await adminService.deleteCompany(company.id);
            setCompanies(prev => prev.filter(c => c.id !== company.id));
            setAlert({ type: 'success', message: t('company_deleted') });
        } catch (e) {
            setAlert({ type: 'error', message: e?.response?.data?.detail || e.message });
        }
    };

    return (
        <div>
            {alert && (
                <div className="mb-4">
                    <Alert {...alert} onClose={() => setAlert(null)} />
                </div>
            )}

            <SectionHeader
                title={t('companies')}
                action={
                    <Btn variant={showAddForm ? 'secondary' : 'primary'} onClick={() => {
                        if (!showAddForm) setAddForm(f => ({ ...f, id: '', name: '', short_id: genShortId() }));
                        setShowAddForm(v => !v);
                    }}>
                        {showAddForm ? t('Cancel') : t('add_company')}
                    </Btn>
                }
            />

            {showAddForm && (
                <form onSubmit={handleAdd} className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded flex gap-3 items-end">
                    <Input label={t('company_id')} required value={addForm.id} onChange={e => setAddForm(f => ({ ...f, id: e.target.value }))} placeholder={t('eg_acme_corp')} />
                    <Input label={t('company_name')} required value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} />
                    <Input label={t('login_id_6_digits')} required inputMode="numeric" maxLength={6} value={addForm.short_id} onChange={e => setAddForm(f => ({ ...f, short_id: parseInt(e.target.value) || '' }))} placeholder={t('six_digit_login_id')} />
                    <Btn type="submit" disabled={saving}>{saving ? t('saving') : t('create')}</Btn>
                </form>
            )}

            {loading ? <Spinner /> : (
                <div className="overflow-x-auto">
                    <table className="w-full border border-gray-200 rounded text-sm">
                        <thead>
                            <tr>
                                <Th>{t('company_id')}</Th>
                                <Th>{t('login_id')}</Th>
                                <Th>{t('name')}</Th>
                                <Th>{t('active')}</Th>
                                <Th>{t('tenant_count')}</Th>
                                <Th>{t('actions')}</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {companies.length === 0 ? (
                                <tr><Td colSpan={6} className="text-center text-gray-400 py-6">{t('no_companies_found')}</Td></tr>
                            ) : companies.map(c => (
                                <React.Fragment key={c.id}>
                                    <tr className="hover:bg-gray-50">
                                        <Td><span className="font-mono text-xs">{c.id}</span></Td>
                                        <Td><span className="font-mono text-sm font-semibold text-green-700">{c.short_id ?? '—'}</span></Td>
                                        <Td>
                                            {editingId === c.id ? (
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        value={editName}
                                                        onChange={e => setEditName(e.target.value)}
                                                        className="border border-gray-300 rounded px-2 py-1 text-sm w-40"
                                                        autoFocus
                                                    />
                                                    <Btn size="xs" onClick={() => handleEditSave(c.id)} disabled={saving}>{t('save')}</Btn>
                                                    <Btn size="xs" variant="ghost" onClick={() => setEditingId(null)}>{t('Cancel')}</Btn>
                                                </div>
                                            ) : (
                                                c.name
                                            )}
                                        </Td>
                                        <Td>
                                            <Toggle checked={!!c.is_active} onChange={() => handleToggleActive(c)} />
                                        </Td>
                                        <Td>{c.tenant_count ?? '—'}</Td>
                                        <Td>
                                            <div className="flex gap-2">
                                                {editingId !== c.id && (
                                                    <Btn size="xs" variant="secondary" onClick={() => { setEditingId(c.id); setEditName(c.name); }}>
                                                        {t('edit_name')}
                                                    </Btn>
                                                )}
                                                <Btn size="xs" variant={expandedCompany === c.id ? 'primary' : 'secondary'}
                                                    onClick={() => setExpandedCompany(expandedCompany === c.id ? null : c.id)}>
                                                    {expandedCompany === c.id ? t('hide_credentials') : t('credentials')}
                                                </Btn>
                                                <Btn size="xs" variant="danger" onClick={() => handleDelete(c)}>
                                                    {t('delete')}
                                                </Btn>
                                            </div>
                                        </Td>
                                    </tr>
                                    {expandedCompany === c.id && (
                                        <tr>
                                            <td colSpan={6} className="bg-gray-50 px-4 py-3 border-t border-gray-100">
                                                <CredentialsPanel tenantId={c.id} />
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

// ---------------------------------------------------------------------------
// SuperAdminPage — root component
// ---------------------------------------------------------------------------

const SuperAdminPage = () => {
    const { t } = useLanguage();
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('tenants');
    const [companies, setCompanies] = useState([]);
    const [companiesLoaded, setCompaniesLoaded] = useState(false);

    // Load companies once — shared between tabs (TenantsTab needs them for the select)
    useEffect(() => {
        adminService.getCompanies()
            .then(data => { setCompanies(data); setCompaniesLoaded(true); })
            .catch(() => setCompaniesLoaded(true));
    }, []);

    const tabs = [
        { id: 'tenants', label: t('tenants') },
        { id: 'companies', label: t('companies') },
    ];

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
                <div className="flex items-center gap-6">
                    <span className="font-semibold text-gray-900 text-sm">{t('super_admin_title')}</span>
                    <nav className="flex gap-1">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                    activeTab === tab.id
                                        ? 'bg-blue-600 text-white'
                                        : 'text-gray-600 hover:bg-gray-100'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </nav>
                </div>
                <Btn variant="secondary" size="sm" onClick={() => { logout(); navigate('/system-access'); }}>{t('logout')}</Btn>
            </header>

            {/* Content */}
            <main className="max-w-6xl mx-auto px-6 py-6">
                {!companiesLoaded ? (
                    <Spinner />
                ) : (
                    <>
                        {activeTab === 'tenants' && <TenantsTab companies={companies} />}
                        {activeTab === 'companies' && <CompaniesTab />}
                    </>
                )}
            </main>
        </div>
    );
};

export default SuperAdminPage;
